import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';

/**
 * Fetches fresh store data and emits current-package-changed event with the store as payload
 *
 * This ensures the store is fetched BEFORE the event is emitted, preventing race conditions
 * where components navigate before the store is updated.
 *
 * @returns Promise that resolves when store is fetched and event is emitted
 * @throws Re-throws any fetch errors after logging (caller should handle)
 *
 * @example
 * ```typescript
 * // In subscription confirmation flow
 * await this.store.updateInstallationStatus();
 * await updateStoreAndEmitPackageChange();  // Fetch store + emit
 * this.$router.push('/ai-report-builder');  // Navigate with fresh store
 * ```
 */
export async function updateStoreAndEmitPackageChange(): Promise<void> {
  // Fetch fresh store data from API
  const storeModel = new StoreModel();

  try {
    await storeModel.fetch();
  } catch (error) {
    console.error('[storeEventHelper] Failed to fetch store data:', error);
    throw error; // Re-throw so callers can decide how to handle
  }

  // Emit event WITH store data as payload
  // App.vue listener will receive this store and assign it
  EventBus.$emit('current-package-changed', storeModel);
}
